import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate } from 'react-router-dom'
import { LogIn } from 'lucide-react'
import axios from 'axios'
import { loginFormSchema, type LoginFormData } from '../types'
import { useAuthStore } from '../stores/authStore'
import { useFormFormatter } from '@/shared/hooks/useFormFormatter'

export function LoginForm() {
  const navigate = useNavigate()
  const login = useAuthStore((s) => s.login)
  const [serverError, setServerError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      emailRut: '',
      password: '',
    },
  })

  const formatterFields = useMemo(() => [
    { name: 'emailRut' as const, formatter: 'emailOrRut' as const },
  ], [])

  useFormFormatter<LoginFormData>(watch, setValue, register, formatterFields)

  const onSubmit = async (data: LoginFormData) => {
    setServerError(null)
    try {
      await login(data.emailRut, data.password)
      navigate('/', { replace: true })
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const detail = error.response?.data?.detail
        setServerError(typeof detail === 'string' ? detail : 'Error de usuario o contraseña')
      } else {
        setServerError('Error de conexión. Intente nuevamente.')
      }
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-primary-700 text-center mb-1">
          Club Torta Caluga
        </h2>
        <p className="text-gray-500 text-center text-lg">Iniciar Sesión</p>
      </div>

      {serverError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {serverError}
        </div>
      )}

      <div>
        <label htmlFor="emailRut" className="block text-sm font-medium text-gray-700 mb-1">
          Email o RUT
        </label>
        <input
          id="emailRut"
          type="text"
          placeholder="Email o RUT"
          autoComplete="username"
          className="input"
          {...register('emailRut')}
        />
        {errors.emailRut && (
          <p className="text-red-500 text-sm mt-1">{errors.emailRut.message}</p>
        )}
      </div>

      <div>
        <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
          Contraseña
        </label>
        <input
          id="password"
          type="password"
          placeholder="Contraseña"
          autoComplete="current-password"
          className="input"
          {...register('password')}
        />
        {errors.password && (
          <p className="text-red-500 text-sm mt-1">{errors.password.message}</p>
        )}
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="btn btn-primary w-full flex items-center justify-center gap-2"
      >
        <LogIn size={18} />
        {isSubmitting ? 'Ingresando...' : 'Ingresar'}
      </button>
    </form>
  )
}
